import {useTranslation} from 'react-i18next';

import {Box, type SxProps, Theme} from '@mui/material';

import {BaseStatCard} from '../../../../../components/BaseStatCard/BaseStatCard';
import {StatsGridContainer} from '../../../../../components/StatsGridContainer/StatsGridContainer';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceDashboardOverview} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardOverview | null;
  fillHeight?: boolean;
}

type KpiItem = {
  key: string;
  title: string;
  value: number | string;
  subtitle: string;
  valueSx?: SxProps<Theme>;
};

const kpiItems = (data: WorkforceDashboardOverview | null, t: (key: string, options?: object) => string): KpiItem[] => [
  {
    key: 'profilesAnalyzed',
    title: t('kpis.profilesAnalyzed.label'),
    value: data?.profilesAnalyzed ?? 0,
    subtitle: t('kpis.profilesAnalyzed.helper', {count: data?.departmentCount ?? 0}),
    valueSx: {color: greenColors[700], fontWeight: 700},
  },
  {
    key: 'averageAlignment',
    title: t('kpis.averageAlignment.label'),
    value: data?.averageAlignmentScore != null ? `${data.averageAlignmentScore}%` : '—',
    subtitle: t('kpis.averageAlignment.helper'),
    valueSx: {color: greenColors[700], fontWeight: 700},
  },
];

export const WorkforceDashboardKpiStrip = ({data, fillHeight}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage'});

  const items = kpiItems(data, t);

  if (fillHeight) {
    return (
      <>
        {items.map((item) => (
          <Box key={item.key} sx={{height: '100%', minHeight: 0}}>
            <BaseStatCard
              fillWidth
              fillHeight
              contentLayout='centered'
              title={item.title}
              value={item.value}
              subtitle={item.subtitle}
              valueSx={item.valueSx}
            />
          </Box>
        ))}
      </>
    );
  }

  return (
    <StatsGridContainer sx={{mb: 0, gridTemplateColumns: '1fr'}}>
      {items.map((item) => (
        <BaseStatCard
          key={item.key}
          fillWidth
          contentLayout='centered'
          title={item.title}
          value={item.value}
          subtitle={item.subtitle}
          valueSx={item.valueSx}
        />
      ))}
    </StatsGridContainer>
  );
};
